import React, { useState, useRef, useEffect, useContext } from "react";
import { message, Spin } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { processDoctorAssessmentApi } from "../../../../../services/doctor.services";
import AiVoiceRecorder from "./AiVoiceRecorder";
import "./VoiceRecorderInitial.css";
import { UserContext } from "../../../../../context/UserContext";

export default function FloatingRecorder({
  onProcessComplete,
  onClose,
  audioFor,
}) {
  const { userDetails } = useContext(UserContext);
  const [recordedFile, setRecordedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoStart, setAutoStart] = useState(true); // 🔥 NEW: Auto-start state
  const voiceRecorderRef = useRef(null); // 🔥 Reference to the recorder

  const submitKey =
    audioFor === "1"
      ? "history"
      : audioFor === "4"
      ? "functional-examination"
      : audioFor === "5"
      ? "clinical-diagnosis"
      : audioFor === "2"
      ? "general-examination"
      : "systemic-examination";

  // 🔥 Cleanup when component unmounts or closes
  useEffect(() => {
    return () => {
      if (voiceRecorderRef.current) {
        voiceRecorderRef.current.stopRecordingAndCleanup();
      }
    };
  }, []);
  useEffect(() => {
    // Since we use key=... in the parent, this component is new.
    // We set autoStart to false after the first render to prevent re-triggering.
    // AiVoiceRecorder will read this state on its first render.
    setAutoStart(false);
  }, []);

  const onRecordSubmit = async () => {
    if (!recordedFile) {
      message.error("No recording found");
      return;
    }

    setLoading(true);
    // CRITICAL: Stop the recorder and tracks *before* making the API call.
    // This ensures the red dot goes away before waiting for the network response.
    if (voiceRecorderRef.current) {
      voiceRecorderRef.current.stopRecordingAndCleanup();
    }
    // ... rest of the submit logic ...

    try {
      const fd = new FormData();
      fd.append("audio_file", recordedFile);

      const response = await processDoctorAssessmentApi(submitKey, fd);
      message.success("Audio processed successfully!");

      onProcessComplete(response?.data || {});
    } catch (error) {
      console.error(error);
      message.error("Failed to process audio.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // 🔥 Stop recording and cleanup before closing (Kept As Is)
    if (voiceRecorderRef.current) {
      voiceRecorderRef.current.stopRecordingAndCleanup();
    }
    onClose();
  };

  return (
    <>
      {/* Semi-transparent Backdrop - NO pointer-events blocking */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          // backgroundColor: "rgba(0, 0, 0, 0.25)",
          zIndex: 1000,
          pointerEvents: "none",
        }}
      />

      {/* Floating Recorder Card */}
      <div
        style={{
          position: "fixed",
          bottom: "58px",
          right: "22px",
          width: "60px",
          height: "220px",
          backgroundColor: "transparent",
          borderRadius: "100px",
          border: `2px solid ${userDetails?.activeOrg?.color || "var(--ant-primary-color, #7F56D9)"}`,
          borderBottom: "none",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "12px",
          padding: "14px 12px 14px 12px",
          pointerEvents: "auto",
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Spin size="large" tip="Processing..." />
          </div>
        ) : (
          <>
            {/* Close Button */}
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                backgroundColor: "#FB2C36",
                border: "2px solid #ff4d4f",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                // backgroundColor: "#fff",
              }}
              onClick={handleClose}
            >
              <CloseOutlined style={{ fontSize: "18px", color: "#fff" }} />
            </div>

            <AiVoiceRecorder
              ref={voiceRecorderRef}
              recordedFile={recordedFile}
              startRecord={autoStart} // 🔥 Pass autoStart for initial trigger
              setStartRecord={setAutoStart} // Pass setter for potential internal use
              onRecordSubmit={onRecordSubmit}
              setRecordedFile={setRecordedFile}
              isFloating={true}
            />

            {/* Submit Button */}
            {recordedFile && (
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  backgroundColor: "#00C950",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  opacity: 1,
                  boxShadow: "0 4px 12px rgba(255, 107, 0, 0.3)",
                  marginTop: "auto",
                }}
                onClick={onRecordSubmit}
              >
                <CheckOutlined style={{ fontSize: "18px", color: "#fff" }} />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
